// API Service for Metapic V2 Frontend
// Connects to AWS Lambda backend via API Gateway
// Fully aligned with API Reference v2.0.0



// API Base URL configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.metapic.app';

// API Response interfaces based on API Reference
interface ApiSuccessResponse<T = any> {
  success: true;
  data: T;
  timestamp: string;
  requestId?: string;
}

interface ApiErrorResponse {
  success: false;
  error: {
    code: string;
    message: string;
    details?: any;
  };
  timestamp: string;
  requestId?: string;
}

type ApiResponse<T = any> = ApiSuccessResponse<T> | ApiErrorResponse;

// Specific response types based on API Reference
interface HealthCheckData {
  message: string;
  timestamp: string;
  version: string;
  environment: string;
}

// LoginData interface removed - authentication now handled by Supabase only

interface RefreshTokenData {
  token: string;
  expires_at: string;
}

interface PresignedUrlData {
  uploadId: string;
  presignedUrl: string;
  s3Key: string;
  expiresIn: number;
  maxFileSize: number;
  allowedTypes: string[];
}

interface FileUploadData {
  uploadId: string;
  status: 'completed';
  confirmedAt: string;
}

interface FileData {
  id: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  fileUrl: string;
  thumbnailUrl?: string;
  status: 'processing' | 'completed' | 'failed';
  uploadedAt: string;
  processedAt?: string;
  metadata?: {
    dimensions?: { width: number; height: number };
    exif?: any;
    aiTags?: string[];
    objects?: Array<{ name: string; confidence: number }>;
  };
}

interface AssetsListData {
  assets: FileData[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

interface UserProfileData {
  user: {
    // Basic user info
    id: string;
    email: string;
    name: string;
    profile_image?: string;
    role: string;
    
    // Credit system
    credits: number;
    
    // Timestamps
    created_at: string;
    updated_at: string;
    
    // User preferences
    preferences?: {
      notifications?: {
        email?: boolean;
        processing_alerts?: boolean;
      };
    };
    
    // Membership info
    membership_status?: string;
    current_package?: string;
    membership_expires_at?: string; // Maps to expired_at from database
    free_account_status?: boolean;
    
    // Account status
    status?: string;
    suspended_at?: string;
    suspended_by?: string;
    banned_at?: string;
    banned_by?: string;
    ban_reason?: string;
    ban_expires_at?: string;
    last_login_at?: string;
  };
  // Note: Stats are now fetched separately via getUserStats() endpoint
  // to ensure data consistency with actual database schema
}

interface CreditCheckData {
  hasEnoughCredits: boolean;
  currentBalance: number;
  requiredCredits: number;
  shortfall?: number;
  assetsValidated: number;
}

interface CreditCheckRequest {
  assets: Array<{
    fileName: string;
    fileSize: number;
    mimeType: string;
  }>;
}

// Credit Reservation interfaces - REMOVED (deadcode)
// These interfaces were never used by the frontend

// Album pause/resume interfaces
interface AlbumPauseData {
  success: boolean;
  albumId: string;
  status: 'paused';
  pausedAt: string;
  gracePeriodExpiresAt: string;
}

interface AlbumResumeData {
  success: boolean;
  albumId: string;
  status: 'active';
  resumedAt: string;
}

interface PausedAlbumsData {
  albums: Array<{
    id: string;
    name: string;
    status: 'paused';
    pausedAt: string;
    gracePeriodExpiresAt: string;
    creditsReserved: number;
  }>;
}

interface AlbumCreationData {
  albumId: string;
  albumSlug: string;
  redirectUrl: string;
  processedAssets?: any[];
}

interface AlbumCreationRequest {
  files: Array<{
    name: string;
    size: number;
    type: string;
  }>;
  settings: {
    maxTitleLength: number;
    maxDescriptionLength: number;
    maxKeywords: number;
  };
  customInstruction?: string;
}

// Album validation interfaces
interface AlbumValidationData {
  valid: boolean;
  album?: {
    id: string;
    title: string;
    slug: string;
    status: string;
    created_at: string;
  };
  assets: {
    total: number;
    pending: number;
    completed: number;
    failed: number;
  };
  hasAccess: boolean;
}

// Album summary for My Uploads page
interface AlbumSummary {
  id: string;
  slug: string;
  title: string;
  createdAt: string;
  fileCount: number;
  totalSize: number;
  status: 'complete' | 'processing' | 'failed';
  firstAssetId?: string;
  thumbnailPath?: string; // Path to first asset for thumbnail
  thumbnailName?: string; // Original filename for alt text
}

// Error codes from API Reference
export enum ApiErrorCode {
  // Authentication & Authorization
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  TOKEN_EXPIRED = 'TOKEN_EXPIRED',
  TOKEN_INVALID = 'TOKEN_INVALID',
  
  // Validation
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  INVALID_REQUEST = 'INVALID_REQUEST',
  MISSING_REQUIRED_FIELD = 'MISSING_REQUIRED_FIELD',
  INVALID_FILE_TYPE = 'INVALID_FILE_TYPE',
  FILE_TOO_LARGE = 'FILE_TOO_LARGE',
  
  // Resources
  NOT_FOUND = 'NOT_FOUND',
  ALREADY_EXISTS = 'ALREADY_EXISTS',
  EMAIL_NOT_VERIFIED = 'EMAIL_NOT_VERIFIED',
  
  // Rate Limiting
  RATE_LIMIT_EXCEEDED = 'RATE_LIMIT_EXCEEDED',
  
  // Server Errors
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
  DATABASE_ERROR = 'DATABASE_ERROR',
  EXTERNAL_SERVICE_ERROR = 'EXTERNAL_SERVICE_ERROR',
  
  // Upload Specific
  UPLOAD_FAILED = 'UPLOAD_FAILED',
  PROCESSING_FAILED = 'PROCESSING_FAILED',
  PRESIGNED_URL_GENERATION_FAILED = 'PRESIGNED_URL_GENERATION_FAILED',
  
  // Credit System
  INSUFFICIENT_CREDITS = 'INSUFFICIENT_CREDITS',
  
  // Frontend specific (for network issues)
  NETWORK_ERROR = 'NETWORK_ERROR',
  REQUEST_TIMEOUT = 'REQUEST_TIMEOUT'
}

// Custom error class for API errors
export class ApiError extends Error {
  public code: string;
  public details?: any;
  public httpStatus?: number;

  constructor(code: string, message: string, details?: any, httpStatus?: number) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.details = details;
    this.httpStatus = httpStatus;
  }
}

class ApiService {
  private baseURL: string;

  constructor() {
    this.baseURL = API_BASE_URL;
  }

  private async makeRequest<T = any>(endpoint: string, options: RequestInit = {}): Promise<ApiSuccessResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;
    const startTime = Date.now();
    
    const defaultHeaders = {
      'Content-Type': 'application/json',
    };

    // Add timeout and abort controller for better error handling
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

    const config: RequestInit = {
      ...options,
      signal: controller.signal,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      clearTimeout(timeoutId);
      
      const contentType = response.headers.get('content-type');
      let responseData: ApiResponse<T>;
      
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
      } else {
        // Handle non-JSON responses
        const textData = await response.text();
        responseData = {
          success: false,
          error: {
            code: 'INVALID_RESPONSE_FORMAT',
            message: `Server returned ${response.status} ${response.statusText}. Expected JSON but got: ${contentType || 'unknown'}`,
            details: { responseText: textData, url, status: response.status }
          },
          timestamp: new Date().toISOString()
        };
      }

      // Handle error responses according to API Reference format
      if (!response.ok || !responseData.success) {
        const errorResponse = responseData as ApiErrorResponse;
        
        // Create more informative error messages based on status codes
        let errorMessage = errorResponse.error?.message || `Server error: ${response.status} ${response.statusText}`;
        
        if (response.status === 401) {
          errorMessage = 'Authentication failed. Please log in again.';
        } else if (response.status === 403) {
          errorMessage = 'Access denied. You do not have permission to perform this action.';
        } else if (response.status === 404) {
          errorMessage = 'The requested resource was not found.';
        } else if (response.status === 429) {
          errorMessage = 'Too many requests. Please wait a moment and try again.';
        } else if (response.status >= 500) {
          // Preserve backend error message if provided
          if (!errorResponse.error?.message) {
            errorMessage = 'Server error. Please try again later.';
          }
        }
        
        throw new ApiError(
          errorResponse.error?.code || 'HTTP_ERROR',
          errorMessage,
          { ...errorResponse.error?.details, url, status: response.status, statusText: response.statusText },
          response.status
        );
      }

      return responseData as ApiSuccessResponse<T>;
    } catch (error) {
      const duration = Date.now() - startTime;
      clearTimeout(timeoutId);
      
      if (error instanceof ApiError) {
        // Add URL context to existing API errors
        error.details = { ...error.details, url, duration };
        console.error(`❌ API Error [${error.code}]:`, error.message, `(${url})`);
        throw error;
      }
      
      // Handle specific error types with more informative messages
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          const timeoutError = new ApiError(
            'REQUEST_TIMEOUT',
            `Request to ${url} timed out after 15 seconds. Please check your connection and try again.`,
            { originalError: error, duration, url }
          );
          console.error('❌ Request timeout:', timeoutError.message);
          throw timeoutError;
        }
        
        if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
          const networkError = new ApiError(
            'NETWORK_ERROR',
            `Failed to connect to ${url}. Please check your internet connection or try again later.`,
            { originalError: error, duration, url }
          );
          console.error('❌ Network error:', networkError.message);
          throw networkError;
        }
        
        if (error.message.includes('CORS')) {
          const corsError = new ApiError(
            'CORS_ERROR',
            `Cross-origin request blocked when accessing ${url}. This may be a server configuration issue.`,
            { originalError: error, duration, url }
          );
          console.error('❌ CORS error:', corsError.message);
          throw corsError;
        }
      }
      
      // Handle other network errors and exceptions
      console.error('❌ Unexpected API error:', error, `(${url})`);
      const unexpectedError = new ApiError(
        'UNEXPECTED_ERROR',
        `An unexpected error occurred while connecting to ${url}. Please try again.`,
        { originalError: error, duration, url }
      );
      throw unexpectedError;
    }
  }

  // Health check endpoint
  async healthCheck(): Promise<ApiSuccessResponse<HealthCheckData>> {
    return this.makeRequest<HealthCheckData>('/health');
  }

  // Authentication endpoints removed - now handled by Supabase only







  async refreshToken(refreshToken: string): Promise<ApiSuccessResponse<RefreshTokenData>> {
    return this.makeRequest<RefreshTokenData>('/auth/refresh', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        refreshToken: refreshToken
      })
    });
  }

  // Logout method removed - logout is handled locally in authService
  // No backend endpoint needed for JWT-based stateless authentication



  // File upload endpoints - using presigned URLs
  // File validation based on API Reference specifications
  private validateFile(file: File): void {
    const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
    const SUPPORTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/tiff', 'image/bmp'];

    if (file.size > MAX_FILE_SIZE) {
      throw new ApiError(
        ApiErrorCode.FILE_TOO_LARGE,
        `File size ${(file.size / 1024 / 1024).toFixed(2)}MB exceeds maximum allowed size of 50MB`,
        { fileSize: file.size, maxSize: MAX_FILE_SIZE }
      );
    }

    if (!SUPPORTED_TYPES.includes(file.type)) {
      throw new ApiError(
        ApiErrorCode.INVALID_FILE_TYPE,
        `File type ${file.type} is not supported. Supported types: ${SUPPORTED_TYPES.join(', ')}`,
        { fileType: file.type, supportedTypes: SUPPORTED_TYPES }
      );
    }
  }

  async getPresignedUrl(fileName: string, fileType: string, fileSize: number, token: string): Promise<ApiSuccessResponse<PresignedUrlData>> {
    return this.makeRequest<PresignedUrlData>('/upload/temp-urls', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        fileName,
        mimeType: fileType, // Backend expects 'mimeType' not 'fileType'
        fileSize,
      }),
    });
  }

  async uploadToS3(presignedUrl: string, file: File): Promise<Response> {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (!response.ok) {
      throw new ApiError(
        ApiErrorCode.UPLOAD_FAILED,
        `Failed to upload file to S3: ${response.statusText}`,
        { status: response.status, statusText: response.statusText }
      );
    }

    return response;
  }

  async confirmUpload(uploadData: {
    uploadId: string;
    fileKey: string;
    fileName: string;
    fileSize: number;
    fileType: string;
  }, token: string): Promise<ApiSuccessResponse<FileUploadData>> {
    return this.makeRequest<FileUploadData>('/upload/confirm', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        uploadId: uploadData.uploadId // Backend only expects uploadId
      }),
    });
  }

  // Complete upload flow with validation
  async uploadFile(file: File, token: string): Promise<ApiSuccessResponse<FileUploadData>> {
    try {
      // Step 0: Validate file before upload
      this.validateFile(file);

      // Step 1: Get presigned URL
      const presignedResponse = await this.getPresignedUrl(
        file.name,
        file.type,
        file.size,
        token
      );

      const { presignedUrl, s3Key, uploadId } = presignedResponse.data;

      // Step 2: Upload to S3
      await this.uploadToS3(presignedUrl, file);

      // Step 3: Confirm upload
      const confirmResponse = await this.confirmUpload({
        uploadId,
        fileKey: s3Key,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      }, token);

      return confirmResponse;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      console.error('Upload process failed:', error);
      throw new ApiError(
        ApiErrorCode.UPLOAD_FAILED,
        error instanceof Error ? error.message : 'Upload process failed',
        { originalError: error }
      );
    }
  }

  // File management endpoints
  async getAssets(token: string, params?: {
    page?: number;
    limit?: number;
    sortBy?: 'uploadedAt' | 'fileName' | 'fileSize';
    sortOrder?: 'asc' | 'desc';
    fileType?: string;
  }): Promise<ApiSuccessResponse<AssetsListData>> {
    let queryString = '';
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, value.toString());
        }
      });
      queryString = searchParams.toString() ? `?${searchParams.toString()}` : '';
    }

    return this.makeRequest<AssetsListData>(`/assets${queryString}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  }

  async getAsset(assetId: string, token: string): Promise<ApiSuccessResponse<FileData>> {
    return this.makeRequest<FileData>(`/assets/${assetId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  }

  async deleteAsset(assetId: string, token: string): Promise<ApiSuccessResponse<{}>> {
    return this.makeRequest<{}>(`/assets/${assetId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  }

  // User management endpoints
  async getUserProfile(token: string): Promise<ApiSuccessResponse<UserProfileData>> {
    return this.makeRequest<UserProfileData>('/user/profile', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
  }

  async getUserStats(token: string): Promise<ApiSuccessResponse<any>> {
     return this.makeRequest<any>('/user/stats', {
       method: 'GET',
       headers: {
         'Authorization': `Bearer ${token}`,
         'Content-Type': 'application/json'
       }
     });
   }

   async updateUserProfile(profileData: {
    name?: string;
    avatar_url?: string;
  }, token: string): Promise<ApiSuccessResponse<UserProfileData>> {
    return this.makeRequest<UserProfileData>('/user/profile', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(profileData),
    });
  }

  async checkCredits(assets: CreditCheckRequest['assets'], token: string): Promise<ApiSuccessResponse<CreditCheckData>> {
     return this.makeRequest<CreditCheckData>('/upload/check-credits', {
       method: 'POST',
       headers: {
         'Content-Type': 'application/json',
         'Authorization': `Bearer ${token}`,
       },
       body: JSON.stringify({ files: assets }),
     });
   }

  // Credit Reservation System endpoints - REMOVED (deadcode)
  // These methods were never used by the frontend

  // Album pause/resume endpoints
  async pauseAlbum(albumId: string, token: string): Promise<ApiSuccessResponse<AlbumPauseData>> {
    return this.makeRequest<AlbumPauseData>(`/albums/${albumId}/pause`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });
  }

  async resumeAlbum(albumId: string, token: string): Promise<ApiSuccessResponse<AlbumResumeData>> {
    return this.makeRequest<AlbumResumeData>(`/albums/${albumId}/resume`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });
  }

  async getPausedAlbums(token: string): Promise<ApiSuccessResponse<PausedAlbumsData>> {
    return this.makeRequest<PausedAlbumsData>('/albums/paused', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  }

  /**
     * Create album entry in database (Phase 1.5)
     * 
     * IMPORTANT: This endpoint does NOT perform actual AI metadata generation!
     * AI metadata generation will be implemented in future phases.
     * 
     * This endpoint calls /upload/create-album which:
     * 1. Validates files and checks user credits
     * 2. Reserves credits for the operation  
     * 3. Creates album entry in database
     * 4. Returns albumId and redirectUrl for Phase 3
     * 
     * The actual AI processing (generating titles, descriptions, keywords)
     * happens later when files are uploaded to S3 and processed by AI engine.
     */
  async createAlbum(request: AlbumCreationRequest, token: string): Promise<ApiSuccessResponse<AlbumCreationData>> {
    
    // Debug: Validate token exists
    
    // Convert frontend request format to backend Phase 1.5 format
    // This calls albumCreationHandler.createAlbum which implements:
    // File Validation First workflow (validates files, creates album, then reserves credits for processing)
    const albumRequest = {
      files: request.files,
      settings: request.settings,
      customInstruction: request.customInstruction
    };

    try {
      const response = await this.makeRequest<AlbumCreationData>('/upload/create-album', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(albumRequest),
      });

      return response;
    } catch (error) {
      console.error('❌ API createAlbum failed:', error);
      
      // Re-throw all errors as-is
      throw error;
    }
  }

  /**
   * Validate album slug - check if album exists and belongs to current user
   */
  async validateAlbumSlug(albumSlug: string, token: string): Promise<ApiSuccessResponse<{ valid: boolean; albumId?: string }>> {
    return this.makeRequest<{ valid: boolean; albumId?: string }>(`/album/validate/${albumSlug}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  }

  /**
   * Validate album with assets - check album ownership and asset count
   */
  async validateAlbum(albumSlug: string, token: string): Promise<ApiSuccessResponse<AlbumValidationData>> {
    return this.makeRequest<AlbumValidationData>(`/results/validate/${albumSlug}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  }

  /**
   * Get completed assets for an album from database
   */
  async getAlbumAssets(albumSlug: string, token: string): Promise<ApiSuccessResponse<{ assets: any[] }>> {
    return this.makeRequest<{ assets: any[] }>(`/albums/${albumSlug}/assets`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  }

  /**
   * Get user's albums with asset count and metadata
   */
  async getUserAlbums(token: string): Promise<ApiSuccessResponse<{ albums: AlbumSummary[] }>> {
    return this.makeRequest<{ albums: AlbumSummary[] }>('/user/albums', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  }

  async deleteAlbum(albumSlug: string, token: string): Promise<ApiSuccessResponse<any>> {
    return this.makeRequest<any>(`/albums/${albumSlug}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
  }

  /**
   * Phase 3 File Re-validation (PV node in mermaid flow)
   * Validates file before upload to prevent SessionStorage tampering
   */
  async validateFileBeforeUpload(validationRequest: {
    fileName: string;
    fileSize: number;
    mimeType: string;
    fileSignature: string;
    fileData?: string; // Only for SVG files
    userId: string;
    sessionId: string;
    fileId: string;
  }, token: string): Promise<ApiSuccessResponse<{
    security: {
      mimeTypeValidated: boolean;
      fileSizeChecked: boolean;
      signatureVerified: boolean;
      contentScanned: boolean;
      svgSanitized: boolean;
      sessionStorageVerified: boolean;
    };
    warnings?: string[];
    processingTime?: string;
  }>> {
    return this.makeRequest('/upload/validate-file', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(validationRequest)
    });
  }

  /**
   * 🔥 UPDATE ALBUM TITLE - BRAND NEW FEATURE! 🔥
   * 
   * This is the FIRST EVER album update functionality in Metapic V2!
   * Simple POST endpoint that actually works!
   * 
   * @param albumSlug - The unique album identifier
   * @param newTitle - The new title for the album
   * @param token - User authentication token
   * @returns Promise with updated album data
   */
  async updateAlbumTitle(albumSlug: string, newTitle: string, token: string): Promise<ApiSuccessResponse<any>> {
    return this.makeRequest<any>('/album/update-title', {
      method: 'POST', // Simple POST - no more PUT bullshit!
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        albumSlug: albumSlug,
        newTitle: newTitle
      })
    });
  }

  /**
   * Get album by slug with settings
   * @param albumSlug - The album slug
   * @param token - User authentication token
   * @returns Promise with album data including settings
   */
  async getAlbumBySlug(albumSlug: string, token: string): Promise<ApiSuccessResponse<{ album: any }>> {
    return this.makeRequest<{ album: any }>(`/albums/${albumSlug}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  }

  // Credit-related endpoints
  async getCreditBalance(token: string): Promise<ApiSuccessResponse<{ balance: number }>> {
    return this.makeRequest<{ balance: number }>('/credits/balance', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  }

  async getPurchaseHistory(token: string, limit: number = 5, offset: number = 0): Promise<ApiSuccessResponse<{
    history: Array<{
      id: number;
      type: string;
      credits_purchased: number;
      price: number;
      status: string;
      created_at: string;
      package_name?: string;
    }>;
    pagination: {
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  }>> {
    const queryParams = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });

    return this.makeRequest<{
      history: Array<{
        id: number;
        type: string;
        credits_purchased: number;
        price: number;
        status: string;
        created_at: string;
        package_name?: string;
      }>;
      pagination: {
        limit: number;
        offset: number;
        hasMore: boolean;
      };
    }>(`/credits/purchase-history?${queryParams}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  }

  async getUsageHistory(token: string, limit: number = 5, offset: number = 0): Promise<ApiSuccessResponse<{
    history: Array<{
      id: string;
      type: string;
      credits_used: number;
      description: string;
      created_at: string;
      metadata?: any;
    }>;
    pagination: {
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  }>> {
    const queryParams = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });

    return this.makeRequest<{
      history: Array<{
        id: string;
        type: string;
        credits_used: number;
        description: string;
        created_at: string;
        metadata?: any;
      }>;
      pagination: {
        limit: number;
        offset: number;
        hasMore: boolean;
      };
    }>(`/credits/usage-history?${queryParams}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  }


  // Test method for debugging database connections
  async makeTestRequest<T = any>(endpoint: string, options: RequestInit = {}): Promise<ApiSuccessResponse<T>> {
    return this.makeRequest<T>(endpoint, options);
  }

  // Utility method to check if error is an API error
  static isApiError(error: any): error is ApiError {
    return error instanceof ApiError;
  }

  // Utility method to extract error message from any error
  static getErrorMessage(error: any): string {
    if (error instanceof ApiError) {
      return error.message;
    }
    if (error instanceof Error) {
      return error.message;
    }
    return 'An unknown error occurred';
  }

  // Utility method to extract error code from API error
  static getErrorCode(error: any): string | null {
    if (error instanceof ApiError) {
      return error.code;
    }
    return null;
  }
}

// Export types for use in other modules
export type {
  ApiResponse,
  ApiSuccessResponse,
  ApiErrorResponse,
  HealthCheckData,
  RefreshTokenData,
  PresignedUrlData,
  FileUploadData,
  FileData,
  AssetsListData,
  UserProfileData,
  CreditCheckData,
  CreditCheckRequest,
  // CreditReservationData, - REMOVED (deadcode)
  // CreditReservationRequest, - REMOVED (deadcode)
  // CommitReservationData, - REMOVED (deadcode)
  // ReleaseReservationData, - REMOVED (deadcode)
  AlbumPauseData,
  AlbumResumeData,
  PausedAlbumsData,
  AlbumCreationData,
  AlbumCreationRequest,
  AlbumValidationData,
  AlbumSummary
};

export const apiService = new ApiService();
export { ApiService };
export default apiService;
